import React, {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';

import HomeBarButton from '../../HomeBarButton/HomeBarButton';
import { FeedbackIcon } from '@icons';
import { useMytaverse } from '@providers/MytaverseProvider';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { env } from 'env';
import { useStyles } from './styles';
import {
  useReportIt,
  ReportFormValues,
  ReportItContext,
} from '@services/reportIt/useReportIt';
import {
  ReportIncidentType,
  ReportAttachmentFile,
  ReportAttachmentPolicy,
  ReportSymptomHint,
  getReportPolicy,
} from '@services/reportIt/reportItApi';

const GAMECAST_VIDEO_ELEMENT_ID = 'gamecastVideoTag';
const MAX_SCREENSHOT_BYTES = 10 * 1024 * 1024;
const MAX_SCREENSHOTS = 3;
const SCREENSHOT_CONTENT_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

const INCIDENT_OPTIONS: Array<{ value: ReportIncidentType; labelKey: string }> = [
  { value: 'black_screen', labelKey: 'reportIssue.incidentTypes.blackScreen' },
  { value: 'audio_only', labelKey: 'reportIssue.incidentTypes.audioOnly' },
  { value: 'frozen_video', labelKey: 'reportIssue.incidentTypes.frozenVideo' },
  { value: 'disconnect', labelKey: 'reportIssue.incidentTypes.disconnected' },
  { value: 'other', labelKey: 'reportIssue.incidentTypes.other' },
];

const SYMPTOM_OPTIONS: Array<{ value: ReportSymptomHint; labelKey: string }> = [
  { value: 'screen_black', labelKey: 'reportIssue.symptoms.screenBlack' },
  {
    value: 'audio_still_playing',
    labelKey: 'reportIssue.symptoms.audioStillPlays',
  },
  { value: 'video_frozen', labelKey: 'reportIssue.symptoms.videoFrozen' },
  { value: 'error_popup', labelKey: 'reportIssue.symptoms.errorMessage' },
  {
    value: 'controls_not_working',
    labelKey: 'reportIssue.symptoms.controlsNotWorking',
  },
  { value: 'disconnected', labelKey: 'reportIssue.symptoms.disconnected' },
  { value: 'other', labelKey: 'reportIssue.symptoms.other' },
];

const DEFAULT_HINTS_BY_INCIDENT: Partial<
  Record<ReportIncidentType, ReportSymptomHint[]>
> = {
  black_screen: ['screen_black', 'audio_still_playing'],
  audio_only: ['audio_still_playing', 'screen_black'],
  frozen_video: ['video_frozen'],
  disconnect: ['disconnected'],
};

export const reportIssueFeatureEnabled = (): boolean =>
  env.REACT_APP_FEATURE_REPORT_IT !== 'false';

const defaultHints = (incidentType: ReportIncidentType): ReportSymptomHint[] =>
  DEFAULT_HINTS_BY_INCIDENT[incidentType] ?? [];

const cleanDescription = (value: string): string | undefined => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const getVideoElement = (): HTMLVideoElement | null =>
  document.getElementById(GAMECAST_VIDEO_ELEMENT_ID) as HTMLVideoElement | null;

const fileToAttachment = (file: File): ReportAttachmentFile => ({
  file,
  filename: file.name,
  contentType: file.type,
  byteSize: file.size,
});

const ReportIssueButton = () => {
  const classes = useStyles();
  const { t: translate } = useTranslation('common');
  const { websocketSessionId } = useMytaverse();
  const {
    currentEvent,
    currentEventId,
    gameCastStreamId,
    gamecastClientRef,
    videoRef,
  } = useMytaverseEvent();

  const [open, setOpen] = useState(false);
  const [incidentType, setIncidentType] =
    useState<ReportIncidentType>('black_screen');
  const [description, setDescription] = useState('');
  const [symptomHints, setSymptomHints] = useState<ReportSymptomHint[]>(
    defaultHints('black_screen'),
  );
  const [attachments, setAttachments] = useState<ReportAttachmentFile[]>([]);
  const [attachmentPolicy, setAttachmentPolicy] =
    useState<ReportAttachmentPolicy | null>(null);
  const [attachmentError, setAttachmentError] = useState<string | undefined>();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const context: ReportItContext = useMemo(
    () => ({
      environment:
        env.REACT_APP_MYTAVERSE_ENV || env.REACT_APP_SENTRY_ENV || 'unknown',
      eventId: currentEvent?.eventId || currentEventId,
      browserSessionId: websocketSessionId || 'unknown-session',
      entities: {
        websocketSessionId,
        streamGroupId: gameCastStreamId || undefined,
      },
      getStats: gamecastClientRef?.current?.getVideoRTCStats
        ? () => gamecastClientRef.current.getVideoRTCStats()
        : undefined,
      getVideoElement: () => videoRef?.current ?? getVideoElement(),
    }),
    [
      currentEvent?.eventId,
      currentEventId,
      websocketSessionId,
      gameCastStreamId,
      gamecastClientRef,
      videoRef,
    ],
  );

  const reportIt = useReportIt(context);
  const isSubmitting = reportIt.status === 'submitting';
  const isSuccess = reportIt.status === 'success';

  const resetForm = useCallback(() => {
    setIncidentType('black_screen');
    setDescription('');
    setSymptomHints(defaultHints('black_screen'));
    setAttachments([]);
    setAttachmentError(undefined);
    reportIt.reset();
  }, [reportIt]);

  const handleOpen = useCallback(() => {
    resetForm();
    setOpen(true);
  }, [resetForm]);

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      setOpen(false);
    }
  }, [isSubmitting]);

  const handleIncidentChange = useCallback(
    (value: ReportIncidentType) => {
      setIncidentType(value);
      setSymptomHints(defaultHints(value));
      reportIt.reset();
    },
    [reportIt],
  );

  const handleSymptomToggle = useCallback((value: ReportSymptomHint) => {
    setSymptomHints((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value],
    );
  }, []);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    let cancelled = false;

    getReportPolicy()
      .then((policy) => {
        if (!cancelled) {
          setAttachmentPolicy(policy.attachmentPolicy);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAttachmentPolicy(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  const attachmentsEnabled = !!attachmentPolicy?.attachmentsEnabled;

  const handleAttachmentSelect = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setAttachmentError(undefined);
      const selected = Array.from(event.target.files ?? []);
      event.target.value = '';

      if (selected.length === 0) {
        return;
      }

      const next = [...attachments];

      for (const file of selected) {
        if (next.length >= MAX_SCREENSHOTS) {
          setAttachmentError(
            translate('reportIssue.errors.maxScreenshots', {
              count: MAX_SCREENSHOTS,
            }),
          );
          break;
        }

        if (!SCREENSHOT_CONTENT_TYPES.includes(file.type)) {
          setAttachmentError(translate('reportIssue.errors.invalidScreenshotType'));
          continue;
        }

        if (file.size > MAX_SCREENSHOT_BYTES) {
          setAttachmentError(translate('reportIssue.errors.screenshotTooLarge'));
          continue;
        }

        next.push(fileToAttachment(file));
      }

      setAttachments(next);
    },
    [attachments, translate],
  );

  const handleRemoveAttachment = useCallback((index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
    setAttachmentError(undefined);
  }, []);

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();

      const values: ReportFormValues = {
        incidentType,
        description: cleanDescription(description),
        symptomHints,
        severityHint:
          incidentType === 'black_screen' || incidentType === 'disconnect'
            ? 'high'
            : undefined,
        attachDiagnostics: true,
        attachments: attachmentsEnabled ? attachments : undefined,
      };

      await reportIt.submit(values);
    },
    [incidentType, description, symptomHints, attachmentsEnabled, attachments, reportIt],
  );

  const stopGameInputPropagation = useCallback((event: React.SyntheticEvent) => {
    event.stopPropagation();
  }, []);

  if (!reportIssueFeatureEnabled()) {
    return null;
  }

  return (
    <>
      <HomeBarButton
        handleClick={handleOpen}
        icon={<FeedbackIcon />}
        label={translate('reportIssue.buttonLabel')}
        dataTestId="report-issue-button"
        disabled={!websocketSessionId}
      />

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: classes.dialogPaper,
        }}
      >
        <Box
          component="form"
          onSubmit={handleSubmit}
          onKeyDown={stopGameInputPropagation}
          onKeyUp={stopGameInputPropagation}
          onPointerDown={stopGameInputPropagation}
          onPointerUp={stopGameInputPropagation}
          onMouseDown={stopGameInputPropagation}
          onMouseUp={stopGameInputPropagation}
        >
          <DialogTitle
            sx={classes.title}
          >
            {translate('reportIssue.title')}
            <IconButton
              aria-label={translate('reportIssue.close')}
              onClick={handleClose}
              disabled={isSubmitting}
              size="small"
              sx={classes.closeButton}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={classes.dialogContent}>
            {isSuccess ? (
              <Box sx={classes.successContent}>
                <Alert severity="success" sx={classes.successAlert}>
                  {translate('reportIssue.success', {
                    reportId: reportIt.result?.reportId,
                  })}
                </Alert>
              </Box>
            ) : (
              <>
                {reportIt.status === 'error' ? (
                  <Alert severity="error">
                    {reportIt.error || translate('reportIssue.errors.submitFailed')}
                  </Alert>
                ) : null}

                <TextField
                  select
                  label={translate('reportIssue.issueType')}
                  value={incidentType}
                  onChange={(event) =>
                    handleIncidentChange(event.target.value as ReportIncidentType)
                  }
                  disabled={isSubmitting}
                  fullWidth
                  sx={classes.field}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: classes.menuPaper,
                      },
                    },
                  }}
                  inputProps={{ 'data-testid': 'report-issue-type' }}
                >
                  {INCIDENT_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {translate(option.labelKey)}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  label={translate('reportIssue.descriptionLabel')}
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  disabled={isSubmitting}
                  fullWidth
                  multiline
                  minRows={4}
                  autoFocus
                  sx={classes.field}
                  inputProps={{ maxLength: 2000 }}
                />

                <FormControl component="fieldset" disabled={isSubmitting}>
                  <Typography
                    component="legend"
                    sx={classes.legend}
                  >
                    {translate('reportIssue.symptomsLabel')}
                  </Typography>
                  <FormGroup
                    sx={classes.symptomGrid}
                  >
                    {SYMPTOM_OPTIONS.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        control={
                          <Checkbox
                            checked={symptomHints.includes(option.value)}
                            onChange={() => handleSymptomToggle(option.value)}
                            sx={classes.checkbox}
                          />
                        }
                        sx={classes.checkboxLabel}
                        label={translate(option.labelKey)}
                      />
                    ))}
                  </FormGroup>
                </FormControl>

                {attachmentsEnabled ? (
                  <Box>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={SCREENSHOT_CONTENT_TYPES.join(',')}
                      multiple
                      hidden
                      onChange={handleAttachmentSelect}
                      data-testid="report-issue-attachments-input"
                    />
                    <Button
                      type="button"
                      startIcon={<AttachFileIcon />}
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isSubmitting || attachments.length >= MAX_SCREENSHOTS}
                      variant="outlined"
                      sx={classes.attachButton}
                    >
                      {translate('reportIssue.attachScreenshot')}
                    </Button>

                    {attachmentError ? (
                      <Alert severity="warning" sx={classes.attachmentWarning}>
                        {attachmentError}
                      </Alert>
                    ) : null}

                    {attachments.length > 0 ? (
                      <Box sx={classes.attachmentList}>
                        {attachments.map((attachment, index) => (
                          <Box
                            key={`${attachment.filename}-${index}`}
                            sx={classes.attachmentItem}
                          >
                            <Typography variant="body2" noWrap>
                              {attachment.filename ||
                                translate('reportIssue.screenshotIndexedName', {
                                  index: index + 1,
                                })}
                            </Typography>
                            <IconButton
                              aria-label={translate('reportIssue.removeAttachment', {
                                filename:
                                  attachment.filename ||
                                  translate('reportIssue.screenshotFallbackName'),
                              })}
                              size="small"
                              onClick={() => handleRemoveAttachment(index)}
                              disabled={isSubmitting}
                              sx={classes.iconButton}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    ) : null}
                  </Box>
                ) : null}
              </>
            )}
          </DialogContent>

          <DialogActions sx={classes.actions}>
            <Button
              onClick={handleClose}
              disabled={isSubmitting}
              sx={classes.cancelButton}
            >
              {isSuccess
                ? translate('reportIssue.close')
                : translate('reportIssue.cancel')}
            </Button>
            {!isSuccess ? (
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                data-testid="report-issue-submit"
                sx={classes.submitButton}
              >
                {isSubmitting
                  ? translate('reportIssue.sending')
                  : translate('reportIssue.submit')}
              </Button>
            ) : null}
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
};

export default ReportIssueButton;
